


import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  CardContent,
  CardHeader,
  CardList,
  ContentTemplate,
  HeaderTemplate,
  IconButton,
  Pill,
  Progress,
  SectionHeader,
  Thumbnail,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import PDFIcon from 'web-library/stories/assets/images/PDF.svg';
import VideoIcon from 'web-library/stories/assets/images/video.svg';
import EmptyIcon from 'web-library/stories/assets/images/empty_component.svg';

import { useGetDocumentDownload } from '../../../../api-refactored/onlineTutorials/useGetDocumentDownload';
import { ExamLevelsAndResourceTypesResponse } from '../../../../api-refactored/onlineTutorials/useGetExamLevelsAndResourceTypes';
import {
  OnlineTutorialsListingContent,
  usePostOnlineTutorials,
} from '../../../../api-refactored/onlineTutorials/usePostOnlineTutorials';
import { KeyValueResponse } from '../../../../api-refactored/typings';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import { ResourceFilterFormData } from '../../../../models';
import { FileUploadState } from '../../../../models/File';

const FLEX_START = 'flex-start';

const useStyles = makeStyles()((theme) =>
({
  inputSelect: {
    marginBottom: theme.spacing(4),
    width: '60%',
  },
  card: {
    width: '368px',
  },
  titleText: {
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  thumbnailContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#b0cae333',
    alignItems: 'center',
    padding: theme.spacing(3),
    paddingTop: theme.spacing(6),
  },
  thumbnailFileName: {
    height: theme.spacing(9),
    lineBreak: 'anywhere',
  },
  emptyResultContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyText: {
    color: '#96A1AB',
  },
  sectionHeaderContainer: {
    display: 'flex',
    alignItems: FLEX_START,
  },
  pill: {
    marginTop: theme.spacing(0.5),
  },
  // Francis, CSS
  cardHeaderRoot: {},
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '0px',
    gap: '24px',
    flex: 'none',
    order: 1,
    alignSelf: 'stretch',
    flexGrow: 0,
  },
  titleArrow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: FLEX_START,
    padding: '0px',
    gap: '10px',
    width: '32px',
    height: '32px',
    flex: 'none',
    order: 1,
    flexGrow: 0,
  },
  subHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: FLEX_START,
    padding: '0px',
    flex: 'none',
    order: 0,
    alignSelf: 'stretch',
    flexGrow: 0,
    color: theme.color.link.linkDefault01,
  },
  descriptionTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: FLEX_START,
    padding: '0px 16px 16px',
    gap: '16px',
    flex: 'none',
    order: 2,
    alignSelf: 'stretch',
    flexGrow: 0,
    color: theme.color.text.textActive,
  },
}));

interface FilterViewProps {
  t: TFunction;
  classes: Record<string,string>;
  formHook: UseFormReturn<ResourceFilterFormData>;
  examLevelData: KeyValueResponse[];
  resourceTypeData: KeyValueResponse[];
}

const FilterView = ({ t, formHook, classes, examLevelData, resourceTypeData }: FilterViewProps) => {
  const { control, formState: { errors } } = formHook;
  return (
    <>
      <ControlledAutocomplete
        className={classes.inputSelect}
        id="input-select-exam-level"
        name="examLevel"
        control={control}
        label={t('usefulInformation->>Listing.ExamLevel')}
        options={examLevelData || []}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        error={!!errors?.examLevel}
        helperText={errors?.examLevel?.message?.toString()}
        disableClearable
        readOnly
      />
      <ControlledAutocomplete
        className={classes.inputSelect}
        id="input-select-resource-type"
        name="resourceType"
        control={control}
        label={t('usefulInformation->>Listing.ResourceType')}
        options={resourceTypeData || []}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        error={!!errors?.resourceType}
        helperText={errors?.resourceType?.message?.toString()}
        disableClearable
        readOnly
      />
    </>
  );
};

interface OnlineTutorialsFiltersProps {
  examAndResourceData: ExamLevelsAndResourceTypesResponse;
}

const UsefulInformationFilters = ({ examAndResourceData }: OnlineTutorialsFiltersProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const resourceTypeDefault = examAndResourceData.resourceTypes.find((v) => v.code === 'ALL');
  const examLevelDefault = examAndResourceData.examLevels.find((v) => v.code === 'NOT_APPLICABLE');
  const formHook = useForm<ResourceFilterFormData>({
    mode: 'onChange',
    defaultValues: {
      resourceType: resourceTypeDefault,
      examLevel: examLevelDefault,
    },
  });
  const { watch } = formHook;
  const { examLevel, resourceType } = watch();

  const [uuid, setUuid] = useState<string>('');
  const [data, setData] = useState<OnlineTutorialsListingContent[]>([]);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);

  useGetDocumentDownload({
    uuid,
    enabled: !!uuid,
    onSuccess: (response) => {
      setUuid('');
      if (response.fileStatus === FileUploadState.SUCCESS) {
        const newWindow = window.open(response.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
      }
    },
    onError: () => {
      setUuid('');
    },
  });

  const { mutate, status } = usePostOnlineTutorials({
    onSuccess: (res) => {
      setIsLoading(false);
      setIsLastPage(res.last);
      setTotalElements(res.totalElements);
      if (page === 0) {
        setData([...res.content]);
      } else {
        setData((d) => [...d, ...res.content]);
      }
    },
  });

  const getData = useCallback(() => {
    mutate({
      examLevels: examLevel ? [examLevel] : [],
      resourceTypes: resourceType && resourceType.code !== 'ALL' ? [resourceType] : [],
      apiParams: {
        pageSize: 12,
        page,
        order: 'asc',
        sort: 'title',
      },
    });
  }, [examLevel, mutate, page, resourceType]);

  useEffect(() => {
    setIsLoading(true);
    setIsLastPage(false);
    setData([]);
    setPage(0);
  }, [examLevel, resourceType]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onLoadMore = () => {
    setPage((p) => p + 1);
  };

  const cardListRow = (row: any) => (
    <Box className={classes.card}>
      <Thumbnail
        iconGraphic={row.original.resourceType === 'PDF Document' ? PDFIcon : VideoIcon}
        fileName={row.original.fileName}
        thumbnailClassName={classes.thumbnailContainer}
        textClassName={classes.thumbnailFileName}
      />
      <CardHeader className={classes.cardHeaderRoot}>
        <Box className={classes.subHeaderContainer}>
          <Typography className={classes.subHeaderContainer} variant="subtitle2">
            {row.original.examLevel}
          </Typography>
        </Box>

        <Box className={classes.titleContainer}>
          <Typography
            variant="h3"
            className={classes.titleText}
            onClick={() => {
              setUuid(row.original.fileKey);
            }}
          >
            {row.original.title}
          </Typography>
          <IconButton
            color="#1473DA"
            name={IconName.arrowRight}
            ariaLabel="announcement-alert-close"
            className={classes.titleArrow}
            onClick={() => {
              setUuid(row.original.fileKey);
            }}
            iconSize="L" />
        </Box>
      </CardHeader>
      <CardContent className={classes.descriptionTextContainer}>
        <Typography variant="body2">
          <Typography>{row.original.description}</Typography>
        </Typography>
      </CardContent>
    </Box>
  );

  const columns = useMemo(
    () => [
      { Header: 'Resource Type', accessor: 'resourceType' },
      { Header: 'File Name', accessor: 'fileName' },
      { Header: 'Title', accessor: 'title' },
      { Header: 'Description', accessor: 'description' },
      { Header: 'Exam Level', accessor: 'examLevel' },
    ],
    []
  );

  return (
    <>
      <HeaderTemplate>
        <FilterView
          t={t}
          classes={classes}
          examLevelData={examAndResourceData.examLevels}
          resourceTypeData={examAndResourceData.resourceTypes}
          formHook={formHook}
        />
      </HeaderTemplate>
      <ContentTemplate type="full">
        <Box className={classes.sectionHeaderContainer}>
          <SectionHeader title={t('usefulInformation->>Listing.Resources')} />
          <Pill className={classes.pill} label={totalElements.toString()} variant="info" />
        </Box>
        {isLoading && <Progress color="secondary" />}
        {data.length && !isLoading ? (
          <CardList
            columns={columns}
            data={data || []}
            options={{
              customCardRender: cardListRow,
              onLoadMore,
              loadMoreLabel: t('usefulInformation->>Listing.LoadMoreResources'),
              loadMoreStatus: status,
              isLastPage,
            }}
          />
        ) : (
          !isLoading && (
            <Box className={classes.emptyResultContainer}>
              <img alt="empty" src={EmptyIcon} />
              <Typography variant="h3" className={classes.emptyText}>
                {t('usefulInformation->>Listing.EmptyResource')}
              </Typography>
            </Box>
          )
        )}
      </ContentTemplate>
    </>
  );
};

export default UsefulInformationFilters;
